import { useLocation, useNavigate } from "react-router-dom";
import { iUseQuery } from "./models/hookInteface";
import { QueryParam } from "./models/models";

/**
 * Custom hook to easily manage URL query parameters in React apps.
 *
 * Provides convenient methods to update one or more query parameters,
 * automatically reflecting changes in the URL without reloading the page.
 *
 * @version 2.0.0
 *
 * @example
 * const { updateQueryParameter, batchUpdateQueryParameters } = useQuery();
 * updateQueryParameter('filter', 'active');
 * batchUpdateQueryParameters([
 *   { paramName: 'sort', value: 'desc' },
 *   { paramName: 'page', value: '2' },
 * ]);
 *
 * @returns {iUseQuery} Object containing methods to manipulate query parameters.
 */
export const useQuery = (): iUseQuery => {
    const navigate = useNavigate();
    const location = useLocation();

    /**
     * Utility function to create updated search query string from provided parameters.
     *
     * @param {QueryParam[]} params - List of query parameters to update.
     * @param {string} currentSearch - Current URL search string.
     * @returns {string} Updated query string.
     */
    const updateParams = (params: QueryParam[], currentSearch: string): string => {
        const searchParams = new URLSearchParams(currentSearch);
        params.forEach(({ paramName, value }) => {
            if (value === null) {
                searchParams.delete(paramName);
            } else {
                searchParams.set(paramName, value);
            }
        });
        return searchParams.toString();
    };

    /**
     * Updates a single query parameter in the URL.
     *
     * @param {string} paramName - Name of the query parameter.
     * @param {string | null} value - Value to set (pass null to remove the parameter).
     */
    const updateQueryParameter = (paramName: string, value: string | null): void => {
        const search = updateParams([{ paramName, value }], location.search);
        navigate({ pathname: location.pathname, search });
    };

    /**
     * Updates multiple query parameters in the URL at once.
     *
     * @param {QueryParam[]} params - Array of query parameters and their values.
     */
    const batchUpdateQueryParameters = (params: QueryParam[]): void => {
        const search = updateParams(params, location.search);
        navigate({ pathname: location.pathname, search });
    };

    return {
        updateQueryParameter,
        batchUpdateQueryParameters,
    };
};